import { Link } from 'react-router-dom';
import type { NotificationStats } from '../api/types.js';
import { useCostThreshold } from '../hooks/use-cost-threshold.js';
import { useNotificationStats } from '../hooks/use-notification-stats.js';

// The head of a server's pages: one line of instruments (this month's cost, the calendar, today's notifications), each
// a way into Settings. Its pages are in the menu (operator, 30/10/2026).

const hm = (iso: string) => new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'Europe/Paris' }).format(new Date(iso));
const usd = (n: number) => `$${n < 100 ? n.toFixed(2).replace(/\.00$/, '') : Math.round(n).toLocaleString('en-US')}`;

/** « In a meeting until 16:00 »: the calendar in a few words */
export function availability(cal: NonNullable<NotificationStats['calendar']>): string | null {
  if (cal.status !== 'ok') return 'Google refused the calendar';
  const until = cal.now.until ? ` until ${hm(cal.now.until)}` : '';
  switch (cal.now.state) {
    case 'busy': return `In a meeting${until}`;
    case 'focus': return `In focus time${until}`;
    case 'away': return `Away${until}`;
    case 'free': return 'Free now';
    default: return null;
  }
}

function Segment({ to, label, tone, children }: { to: string; label: string; tone?: string; children: string }) {
  return (
    <Link to={to} aria-label={label}
      className="whitespace-nowrap py-0.5 hover:underline focus-visible:outline focus-visible:outline-2"
      style={{ color: tone ?? 'var(--color-text-2)' }}>
      {children}
    </Link>
  );
}

export function ServerStatusLine({ serverId }: { serverId: string }) {
  const { data: cost } = useCostThreshold(serverId);
  const { data: stats } = useNotificationStats(serverId);
  const settings = `/servers/${serverId}/settings`;
  const segments: Array<{ key: string; node: React.ReactNode; phone: boolean }> = [];
  if (cost?.threshold && cost.monthToDate) {
    const over = cost.monthToDate.costUsd >= cost.threshold.monthlyUsd;
    segments.push({ key: 'cost', phone: true, node: (
      <Segment to={`${settings}#cost-title`} label="This month's cost, in settings" tone={over ? 'var(--color-error)' : undefined}>
        {`${usd(cost.monthToDate.costUsd)} of ${usd(cost.threshold.monthlyUsd)} this month`}
      </Segment>
    ) });
  }
  const cal = stats?.calendar ? availability(stats.calendar) : null;
  if (cal) {
    segments.push({ key: 'cal', phone: true, node: (
      <Segment to={`${settings}#notify-title`} label="Calendar availability, in settings" tone={stats?.calendar?.status !== 'ok' ? 'var(--color-warn)' : undefined}>{cal}</Segment>
    ) });
  }
  if (stats) {
    segments.push({ key: 'notif', phone: false, node: (
      <Segment to={`${settings}#notify-title`} label="Notifications today, in settings">
        {`${stats.today} of ${stats.settings.dailyCap} notifications today`}
      </Segment>
    ) });
  }
  if (!segments.length) return null;
  return (
    <p className="mt-1 flex flex-wrap items-center text-sm tabular-nums">
      {segments.map((s, i) => (
        <span key={s.key} className={`${s.phone ? 'flex' : 'hidden sm:flex'} items-center`}>
          {i > 0 && <span aria-hidden="true" className="mx-3 h-3.5 w-px" style={{ backgroundColor: 'var(--color-border-2)' }} />}
          {s.node}
        </span>
      ))}
    </p>
  );
}
